import { useCallback, useEffect, useState } from 'react';
import type { AdminListResult, OrganizationSummary } from 'minka-shared';
import * as adminApi from '@/services/admin-api';
import { describeAdminError } from '@/utils/api-error';

const EMPTY: AdminListResult<OrganizationSummary> = { rows: [], truncated: false };

export interface UseAdminOrganizationsResult {
  data: AdminListResult<OrganizationSummary>;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  rename: (organizationId: string, name: string) => Promise<void>;
  remove: (organizationId: string) => Promise<void>;
  clearError: () => void;
}

/**
 * Every organization, for the Islands tab's group headers and the island
 * modal's picker (§4.3).
 *
 * The islands list alone cannot produce these: an organization that owns no
 * island has no island row to be derived from, and it is exactly the one a
 * superadmin may delete (§6.6).
 */
export function useAdminOrganizations(search: string): UseAdminOrganizationsResult {
  const [data, setData] = useState<AdminListResult<OrganizationSummary>>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      setData(await adminApi.listOrganizations(search));
      setError(null);
    } catch (loadError) {
      setError(describeAdminError(loadError, 'Failed to load organizations'));
    } finally {
      setIsLoading(false);
    }
  }, [search]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const rename = useCallback(async (organizationId: string, name: string) => {
    setError(null);
    try {
      const organization = await adminApi.renameOrganization(organizationId, name);
      setData(previous => ({
        ...previous,
        rows: previous.rows.map(existing =>
          existing.id === organization.id ? organization : existing
        )
      }));
    } catch (renameError) {
      setError(describeAdminError(renameError, 'Failed to rename the organization'));
      throw renameError;
    }
  }, []);

  const remove = useCallback(
    async (organizationId: string) => {
      setError(null);
      try {
        await adminApi.deleteOrganization(organizationId);
      } catch (deleteError) {
        setError(describeAdminError(deleteError, 'Failed to delete the organization'));
        throw deleteError;
      }
      await refresh();
    },
    [refresh]
  );

  const clearError = useCallback(() => setError(null), []);

  return { data, isLoading, error, refresh, rename, remove, clearError };
}
